import React from 'react';

import { ComboBoxField } from '../../../components/datafields/ComboBoxField';
import { BUSINESS_FIELD_OPTIONS } from '../../../constants/businessFieldOptions';
import { LANGUAGE_OPTIONS } from '../../../constants/languageOptions';

interface UserInfoFormProps {
  formData: {
    name: string;
    companyName: string;
    businessField: string;
    preferredLanguage: string;
  };
  auth0Email: string;
  updating: boolean;
  onFieldChange: (field: string, value: string) => void;
  onSave: (e: React.FormEvent) => void;
  onCancel: () => void;
}

const UserInfoForm: React.FC<UserInfoFormProps> = ({
  formData,
  auth0Email,
  updating,
  onFieldChange,
  onSave,
  onCancel,
}) => {
  return (
    <form onSubmit={onSave} className='w-full'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
        {/* Left Column */}
        <div className='space-y-4'>
          {/* Name Input */}
          <div>
            <label
              htmlFor='name-input'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Name
            </label>
            <input
              id='name-input'
              type='text'
              value={formData.name}
              onChange={e => onFieldChange('name', e.target.value)}
              placeholder='Enter your name'
              required
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          {/* Email (Read-only) */}
          <div>
            <span className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Email
            </span>
            <p className='text-gray-600 dark:text-gray-400'>{auth0Email}</p>
          </div>
        </div>

        {/* Right Column */}
        <div className='space-y-4'>
          {/* Company Name Input (Optional) */}
          <div>
            <label
              htmlFor='company-name-input'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Company Name{' '}
              <span className='text-gray-500 text-xs'>(optional)</span>
            </label>
            <input
              id='company-name-input'
              type='text'
              value={formData.companyName}
              onChange={e => onFieldChange('companyName', e.target.value)}
              placeholder='Enter your company name'
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          {/* Business Field Input (Optional) */}
          <div>
            <label
              htmlFor='business-field-input'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Business Field / Industry{' '}
              <span className='text-gray-500 text-xs'>(optional)</span>
            </label>
            <ComboBoxField
              label=''
              value={formData.businessField}
              onChange={value => onFieldChange('businessField', value)}
              isEditing={true}
              options={BUSINESS_FIELD_OPTIONS}
              placeholder='Select or type your business field...'
              inputWidth='w-full'
              popoverWidth='w-96'
            />
          </div>

          {/* Language Preference (Optional) */}
          <div>
            <label
              htmlFor='language-preference-input'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Language Preference{' '}
              <span className='text-gray-500 text-xs'>(optional)</span>
            </label>
            <ComboBoxField
              label=''
              value={formData.preferredLanguage}
              onChange={value => onFieldChange('preferredLanguage', value)}
              isEditing={true}
              options={LANGUAGE_OPTIONS}
              placeholder='Select or type your preferred language...'
              inputWidth='w-full'
              popoverWidth='w-96'
            />
          </div>
        </div>
      </div>

      {/* Save/Cancel Buttons */}
      <div className='flex justify-end gap-2 pt-4'>
        <button
          type='button'
          onClick={onCancel}
          disabled={updating}
          className='px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={updating}
          className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
        >
          {updating ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default UserInfoForm;
